/* eslint-disable import/no-extraneous-dependencies */
import { json2csv } from "json-2-csv";
import * as XLSX from "xlsx";

/**
 * Convert data to CSV format
 * @param {Array<any>} data - Data (as JSON) to be converted
 * @param {object} options - Options for the conversion
 * @returns {string} - CSV string
 */
export async function convertToCSV(data, options) {
	// Generate CSV
	const csv = await json2csv(data, options);
	return csv;
}

/**
 * Convert data to XLSX format
 * @param {Array<any>} data - Data (as JSON) to be converted
 * @param {object} options - Options for the conversion
 * @param {string} options.sheetName - Name of the sheet (default: "Sheet1")
 * @param {boolean} options.skipHeader - Skip header row (default: false)
 * @returns {ArrayBuffer} - XLSX ArrayBuffer
 */
export function convertToXLSX(data, options = {}) {
	const { sheetName = "Sheet1", skipHeader = false, ...xlsxOptions } = options;

	// Create a new workbook
	const workbook = XLSX.utils.book_new();

	// Convert JSON data to worksheet
	const worksheet = XLSX.utils.json_to_sheet(data, { skipHeader });

	// Append worksheet to workbook
	XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

	// Generate XLSX buffer
	const xlsxBuffer = XLSX.write(workbook, {
		bookType: "xlsx",
		type: "array",
		...xlsxOptions
	});

	return xlsxBuffer;
}

/**
 * Download data as CSV or XLSX file
 * @param {Array<any>} data - Data (as JSON) to be converted
 * @param {object} options - Options for the conversion
 * @param {string} filename - Name of the file to be downloaded
 * @param {"csv"|"xlsx"} format - Output format (default: "csv")
 * @returns {void} - No return value, triggers download
 */
export async function downloadSheet(data, options = {}, filename = "export", format = "csv") {
	let blob;
	let fullFilename;

	if (format === "xlsx") {
		const xlsxBuffer = convertToXLSX(data, options);
		blob = new Blob([xlsxBuffer], {
			type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
		});
		// If filename finish with .csv, remove it
		if (filename.endsWith(".csv")) {
			filename = filename.slice(0, -4);
		}
		fullFilename = filename.endsWith(".xlsx") ? filename : `${filename}.xlsx`;
	} else {
		const csv = await convertToCSV(data, {
			excelBOM: true,
			...options
		});
		blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
		// If filename finish with .xlsx, remove it
		if (filename.endsWith(".xlsx")) {
			filename = filename.slice(0, -5);
		}
		fullFilename = filename.endsWith(".csv") ? filename : `${filename}.csv`;
	}

	// Download using native downloader JS
	const link = document.createElement("a");
	if (link.download !== undefined) {
		// feature detection
		const url = URL.createObjectURL(blob);
		link.setAttribute("href", url);
		link.setAttribute("download", fullFilename);
		link.style.visibility = "hidden";
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	}
}

/**
 * Download data as CSV file (legacy function, use downloadSheet instead)
 * @param {Array<any>} data - Data (as JSON) to be converted
 * @param {object} options - Options for the conversion
 * @param {string} filename - Name of the file to be downloaded
 * @returns {void} - No return value, triggers download
 * @deprecated Use downloadSheet with format="csv" instead
 */
export async function downloadCSV(data, options = {}, filename = "export.csv") {
	return downloadSheet(data, options, filename, "csv");
}
